import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { checkRateLimit, recordSubmission } from '../../utils/rateLimiter';
import './ContactForm.css';

const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const honeypotRef = useRef(null);
  
  // Honeypot field - hidden from users but bots will fill it
  const [honeypot, setHoneypot] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Honeypot check - if filled, it's likely a bot
      if (honeypot.trim() !== '') {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.general') 
        });
        setIsSubmitting(false);
        return;
      }

      // Client-side rate limiting
      const rateLimitCheck = checkRateLimit();
      if (!rateLimitCheck.allowed) {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.rateLimit', { 
            minutes: rateLimitCheck.remainingTime 
          }) || `Too many submissions. Please try again in ${rateLimitCheck.remainingTime} minutes.`
        });
        setIsSubmitting(false);
        return;
      }

      // Validate form
      if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.required') 
        });
        setIsSubmitting(false);
        return;
      }

      // Validate field lengths
      if (formData.name.trim().length > 200) {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.nameTooLong') || 'Name is too long (max 200 characters).'
        });
        setIsSubmitting(false);
        return;
      }

      if (formData.email.trim().length > 200) {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.emailTooLong') || 'Email is too long (max 200 characters).'
        });
        setIsSubmitting(false);
        return;
      }

      if (formData.message.trim().length > 5000) {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.messageTooLong') || 'Message is too long (max 5000 characters).'
        });
        setIsSubmitting(false);
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.email') 
        });
        setIsSubmitting(false);
        return;
      }

      // Check consent
      if (!consent) {
        setStatus({ 
          type: 'error', 
          message: t('contact.form.error.consentRequired') 
        });
        setIsSubmitting(false);
        return;
      }

      // Get IP address (optional, for server-side rate limiting)
      let ipAddress = null;
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch (ipError) {
        // If IP fetch fails, continue without it (not critical)
        console.warn('Could not fetch IP address:', ipError);
      }

      // Save to Firestore
      await addDoc(collection(db, 'contactSubmissions'), {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        message: formData.message.trim(),
        consent: true,
        consentDate: serverTimestamp(),
        timestamp: serverTimestamp(),
        read: false,
        ...(ipAddress && { ipAddress }) // Only include if we got an IP
      });

      // Record submission for rate limiting
      recordSubmission();

      // Success
      setStatus({ 
        type: 'success', 
        message: t('contact.form.success') 
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        message: ''
      });
      setConsent(false);
      setHoneypot('');

    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Handle specific Firestore errors
      let errorMessage = t('contact.form.error.general');
      if (error.code === 'permission-denied') {
        errorMessage = t('contact.form.error.rateLimit', { minutes: 60 }) || 
          'Too many submissions. Please try again later.';
      } else if (error.code === 'resource-exhausted') {
        errorMessage = t('contact.form.error.rateLimit', { minutes: 60 }) || 
          'Service temporarily unavailable. Please try again later.';
      }
      
      setStatus({ 
        type: 'error', 
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="name">{t('contact.form.name')} *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder={t('contact.form.namePlaceholder')}
          maxLength={200}
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">{t('contact.form.email')} *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder={t('contact.form.emailPlaceholder')}
          maxLength={200}
        />
      </div>

      <div className="form-group">
        <label htmlFor="message">{t('contact.form.message')} *</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows="4"
          placeholder={t('contact.form.messagePlaceholder')}
          maxLength={5000}
        />
      </div>

      {/* Honeypot field - hidden from users */}
      <div style={{ 
        position: 'absolute', 
        left: '-9999px', 
        opacity: 0, 
        pointerEvents: 'none' 
      }}>
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          ref={honeypotRef}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="consent-wrapper">
        <input
          type="checkbox"
          id="consent-checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
          className="consent-checkbox"
        />
        <label htmlFor="consent-checkbox" className="consent-label">
          {t('contact.form.consent')}{' '}
          <a 
            href="/privacy" 
            target="_blank"
            rel="noopener noreferrer"
            className="privacy-link"
          >
            {t('contact.form.privacyPolicy')}
          </a>
        </label>
      </div>

      {status.message && (
        <div className={`form-status ${status.type}`}>
          {status.message}
        </div>
      )}

      <button 
        type="submit" 
        className="btn-submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
      </button>
    </form>
  );
};

export default ContactForm;

